/* Greedy — Lineup scroll experience (sections/greedy-lineup-scroll.liquid)
 * Handles:
 *  - scroll reveals (IntersectionObserver, respects prefers-reduced-motion)
 *  - progress rail visibility + active chapter
 *  - variant chip -> price / button sync
 *  - AJAX add-to-cart (falls back to a normal form POST without JS)
 * Scoped per section, idempotent on theme-editor reloads.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----- Scroll reveals ----- */

  function initReveals(root) {
    var targets = root.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ----- Progress rail ----- */

  function initRail(root) {
    var rail = root.querySelector('[data-lineup-rail]');
    if (!rail || !('IntersectionObserver' in window)) return;

    var dots = Array.prototype.slice.call(rail.querySelectorAll('[data-lineup-rail-dot]'));
    var chapters = Array.prototype.slice.call(root.querySelectorAll('[data-lineup-chapter]'));
    if (!chapters.length) return;

    // Show the rail only while the lineup section is on screen.
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        rail.classList.toggle('is-visible', entry.isIntersecting);
      });
    }).observe(root);

    // Highlight the chapter crossing the middle of the viewport.
    var activeObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var id = entry.target.id;
          dots.forEach(function (dot) {
            dot.classList.toggle('is-active', dot.getAttribute('data-target') === id);
          });
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    chapters.forEach(function (chapter) { activeObserver.observe(chapter); });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var target = document.getElementById(dot.getAttribute('data-target'));
        if (!target) return;
        target.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });
  }

  /* ----- Buy cards ----- */

  function moneyMarkup(price, comparePrice) {
    var markup = '';
    if (comparePrice) {
      markup += '<s class="greedy-lineup__price-compare">' + comparePrice + '</s> ';
    }
    return markup + price;
  }

  // The cart API body. Line-item properties (the presale "Ships:" line the
  // section renders as a hidden input) ride along as the properties object,
  // the same as a native form post would carry them.
  function lineupPayload(formData, variantId) {
    var payload = {
      id: Number(variantId),
      quantity: Number(formData.get('quantity')) || 1
    };
    formData.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (!match) return;
      payload.properties = payload.properties || {};
      payload.properties[match[1]] = value;
    });
    return payload;
  }

  function initBuyForms(root) {
    root.querySelectorAll('[data-lineup-form]').forEach(function (form) {
      var buyCard = form.closest('.greedy-lineup__buy');
      var priceEl = buyCard ? buyCard.querySelector('[data-lineup-price]') : null;
      var atc = form.querySelector('[data-lineup-atc]');
      var atcLabel = form.querySelector('[data-lineup-atc-label]');
      var atcPrice = form.querySelector('[data-lineup-atc-price]');
      var message = form.querySelector('[data-lineup-message]');
      var idleLabelHTML = atcLabel ? atcLabel.innerHTML : '';

      // Variant chips -> price + button sync
      form.querySelectorAll('[data-lineup-variant]').forEach(function (input) {
        input.addEventListener('change', function () {
          if (priceEl) {
            priceEl.innerHTML = moneyMarkup(input.dataset.price, input.dataset.comparePrice);
          }
          if (atcPrice) atcPrice.textContent = input.dataset.price;
          idleLabelHTML = atcLabel ? atcLabel.innerHTML : idleLabelHTML;
          if (message) message.textContent = '';
        });
      });

      form.addEventListener('submit', function (event) {
        if (!window.fetch || !atc || atc.disabled) return; // native POST fallback
        event.preventDefault();

        var formData = new FormData(form);
        var variantId = formData.get('id');
        if (!variantId) return;

        atc.classList.add('is-loading');
        if (atcLabel) atcLabel.textContent = 'Adding…';
        if (message) message.textContent = '';

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart/add.js'
          : '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(lineupPayload(formData, variantId))
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            atc.classList.remove('is-loading');
            if (!result.ok) {
              var description = result.data && (result.data.description || result.data.message);
              throw new Error(description || 'Something went wrong. Please try again.');
            }

            // Let the theme + cart apps refresh themselves.
            document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
            if (window.greedyOpenCart) window.greedyOpenCart();

            atc.classList.add('is-success');
            if (atcLabel) atcLabel.textContent = 'Added to bag ✓';
            window.setTimeout(function () {
              atc.classList.remove('is-success');
              if (atcLabel) atcLabel.innerHTML = idleLabelHTML;
            }, 2500);
          })
          .catch(function (error) {
            atc.classList.remove('is-loading');
            if (atcLabel) atcLabel.innerHTML = idleLabelHTML;
            if (message) message.textContent = error.message;
          });
      });
    });
  }

  /* ----- Bootstrapping ----- */

  function initSection(root) {
    if (root.__greedyLineupBound) return;
    root.__greedyLineupBound = true;
    initReveals(root);
    initRail(root);
    initBuyForms(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-greedy-lineup]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function (e) { initAll(e.target); });
})();
